import { useState } from 'react';
import { ApiError, adminApi, type ProposalStatus } from '../../lib/api';
import ConfirmModal from '../ConfirmModal';
import SheetDialog from '../SheetDialog';
import { useToast } from '../Toast';

type Action = 'publish' | 'unpublish' | 'archive' | null;

/** Publish / unpublish / archive / rotate-slug, each with a one-step
 *  confirm (rotate-slug gets its own richer modal). Calls `onChanged` after
 *  any successful mutation so the caller can reload the proposal. Every
 *  action flushes pending autosave edits first and bails out if that flush
 *  fails, so a lifecycle change never races (or silently drops) unsaved
 *  content. */
export default function LifecycleActions({
  proposalId,
  slug,
  status,
  onChanged,
  flushPending,
}: {
  proposalId: string;
  slug: string;
  status: ProposalStatus;
  onChanged: () => void;
  flushPending: () => Promise<boolean>;
}) {
  const { showToast } = useToast();
  const [pending, setPending] = useState<Action>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rotateOpen, setRotateOpen] = useState(false);

  async function run(action: NonNullable<Action>) {
    setBusy(true);
    setError(null);
    try {
      if (!(await flushPending())) {
        const message = 'Your latest edits could not be saved. Fix that, then try again.';
        setError(message);
        showToast('error', message);
        return;
      }
      if (action === 'publish') await adminApi.publish(proposalId);
      if (action === 'unpublish') await adminApi.unpublish(proposalId);
      if (action === 'archive') await adminApi.archive(proposalId);
      setPending(null);
      showToast('success', ACTION_COPY[action].success);
      onChanged();
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : 'That action failed.';
      setError(message);
      showToast('error', message);
    } finally {
      setBusy(false);
    }
  }

  const canPublish = status === 'draft' || status === 'archived';
  const canUnpublish = status === 'published';
  const canArchive = status === 'draft' || status === 'published';

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canPublish && (
        <ActionButton onClick={() => setPending('publish')}>Publish</ActionButton>
      )}
      {canUnpublish && (
        <ActionButton onClick={() => setPending('unpublish')}>Unpublish</ActionButton>
      )}
      {canArchive && (
        <ActionButton onClick={() => setPending('archive')} tone="quiet">
          Archive
        </ActionButton>
      )}
      <ActionButton onClick={() => setRotateOpen(true)} tone="quiet">
        Rotate link
      </ActionButton>

      <ConfirmModal
        open={pending !== null}
        title={pending ? ACTION_COPY[pending].title : ''}
        message={pending ? ACTION_COPY[pending].message : ''}
        confirmLabel={pending ? ACTION_COPY[pending].confirm : 'Confirm'}
        busy={busy}
        errorMessage={error}
        onConfirm={() => pending && run(pending)}
        onCancel={() => {
          if (busy) return;
          setPending(null);
          setError(null);
        }}
      />

      {rotateOpen && (
        <RotateSlugModal
          proposalId={proposalId}
          currentSlug={slug}
          flushPending={flushPending}
          onClose={() => setRotateOpen(false)}
          onRotated={() => {
            setRotateOpen(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

const ACTION_COPY: Record<
  NonNullable<Action>,
  { title: string; message: string; confirm: string; success: string }
> = {
  publish: {
    title: 'Publish this proposal',
    message: 'The live link becomes reachable and the page starts serving from the current draft.',
    confirm: 'Publish',
    success: 'Proposal published. The client link is live.',
  },
  unpublish: {
    title: 'Unpublish this proposal',
    message: 'The live link stops serving and the proposal moves back to Draft.',
    confirm: 'Unpublish',
    success: 'Proposal unpublished. The client link is offline.',
  },
  archive: {
    title: 'Archive this proposal',
    message: 'It moves out of the active list. You can publish it again later.',
    confirm: 'Archive',
    success: 'Proposal archived. You can publish it again later.',
  },
};

function ActionButton({
  onClick,
  tone = 'solid',
  children,
}: {
  onClick: () => void;
  tone?: 'solid' | 'quiet';
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        tone === 'solid'
          ? 'rounded-full bg-accent px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-accent-deep'
          : 'rounded-full border border-line px-4 py-1.5 text-sm font-medium text-ink-soft transition hover:border-ink-faint'
      }
    >
      {children}
    </button>
  );
}

function RotateSlugModal({
  proposalId,
  currentSlug,
  flushPending,
  onClose,
  onRotated,
}: {
  proposalId: string;
  currentSlug: string;
  flushPending: () => Promise<boolean>;
  onClose: () => void;
  onRotated: () => void;
}) {
  const [mode, setMode] = useState<'auto' | 'custom'>('auto');
  const [customSlug, setCustomSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  async function rotate() {
    setBusy(true);
    setError(null);
    try {
      if (!(await flushPending())) {
        setError('Your latest edits could not be saved. Fix that, then try again.');
        return;
      }
      const response = await adminApi.rotateSlug(proposalId, mode === 'custom' ? customSlug.trim() : undefined);
      setResult(response.slug);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not rotate the link.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SheetDialog open title={result ? 'Link rotated' : 'Rotate the live link'} onClose={result ? onRotated : onClose} busy={busy}>
      <div>
        {result ? (
          <>
            <p className="mt-3 text-sm text-ink-soft">
              The new live link is <span className="font-mono text-ink">/{result}/</span>. The old link,{' '}
              <span className="font-mono text-ink-faint">/{currentSlug}/</span>, keeps redirecting for 30 days.
            </p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  onRotated();
                }}
                className="rounded-full bg-accent px-4 py-2 text-sm font-semibold text-white transition hover:bg-accent-deep"
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-ink-soft">
              Current link: <span className="font-mono text-ink">/{currentSlug}/</span>
            </p>

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setMode('auto')}
                className={
                  'rounded-full border px-3.5 py-1.5 text-xs font-medium transition ' +
                  (mode === 'auto' ? 'border-accent bg-accent-tint text-accent-deep' : 'border-line text-ink-soft')
                }
              >
                Generate automatically
              </button>
              <button
                type="button"
                onClick={() => setMode('custom')}
                className={
                  'rounded-full border px-3.5 py-1.5 text-xs font-medium transition ' +
                  (mode === 'custom' ? 'border-accent bg-accent-tint text-accent-deep' : 'border-line text-ink-soft')
                }
              >
                Choose a slug
              </button>
            </div>

            {mode === 'custom' && (
              <input
                className="mt-4 w-full rounded-xl border border-line bg-white px-4 py-2.5 font-mono text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/15"
                aria-label="New slug"
                placeholder="new-slug"
                value={customSlug}
                onChange={(event) => setCustomSlug(event.target.value)}
              />
            )}

            {error && <p className="mt-4 text-sm text-error">{error}</p>}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-full border border-line px-4 py-2 text-sm font-medium text-ink-soft transition hover:border-ink-faint disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={rotate}
                disabled={busy || (mode === 'custom' && !customSlug.trim())}
                className="rounded-full bg-accent px-4 py-2 text-sm font-semibold text-white transition hover:bg-accent-deep disabled:cursor-not-allowed disabled:opacity-40"
              >
                {busy ? 'Rotating…' : 'Rotate link'}
              </button>
            </div>
          </>
        )}
      </div>
    </SheetDialog>
  );
}
